import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, getMinutes } from 'date-fns';
import {
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/system';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { Chip, Tooltip } from '@mui/material';

const getStatusStyles = status => {
  const s = String(status).toLowerCase();
  if (s.includes('approved')) {
    return {
      bg: '#e8f5e9',
      text: '#2e7d32',
      border: '#c8e6c9',
      label: s === '0' ? 'Pending' : status
    };
  }
  if (s === '0' || s.includes('pending')) {
    return {
      bg: '#fff3e0',
      text: '#ef6c00',
      border: '#ffe0b2',
      label: 'Pending'
    };
  }
  if (s === '3' || s.includes('cancelled')) {
    return {
      bg: '#f5f5f5',
      text: '#616161',
      border: '#e0e0e0',
      label: 'Cancelled'
    };
  }
  // Default/Rejected
  return {
    bg: '#ffebee',
    text: '#c62828',
    border: '#ffcdd2',
    label: status
  };
};

const cardBaseStyles = {
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'all 0.2s ease-in-out',
  cursor: 'default',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    transform: 'translateY(-1px)',
  }
};

const BookingCard = ({
  bookingName,
  timeDuration,
  id,
  reservationList,
  marginStart = 0,
  type = true,
}) => {
  const [idData, setId] = useState(0);
  const [why, setWhy] = useState(false);
  const [whyValue, setWhyValue] = useState('');
  const [booking, setBooking] = useState(bookingName);
  const [timeDur, setTime] = useState(timeDuration);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [userName, setUserName] = useState('');
  const [marginAdd, setMargin] = useState(marginStart);

  const statusStyles = getStatusStyles(approvalStatus);
  const [openDialog, setOpenDialog] = useState(false);

  const whyOptions = [
    'Machine Not Working',
    'Previous Booking extension',
    'Machine Unavailable',
    'Other',
  ];

  const StyledFormControl = styled(FormControl)(({ theme }) => ({
    margin: theme.spacing(2, 0),
    minWidth: 120,
    '& .MuiInputLabel-root': {
      color: '#666',
      fontWeight: 500,
    },
    '& .MuiInputBase-root': {
      borderRadius: 8,
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      transition: 'box-shadow 0.3s ease-in-out',
      '&:hover': {
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
      },
    },
    '& .MuiSelect-select': {
      padding: theme.spacing(1.5),
    },
  }));

  useEffect(() => {
    if (reservationList[idData]) {
      const data = reservationList[idData];
      setBooking(data.name);
      const timediff = (
        (new Date(data.end_time) - new Date(data.start_time)) /
        (60000 * 60)
      ).toFixed(2);
      setTime(timediff);
      setStartTime(format(new Date(data.start_time), 'HH:mm'));
      setEndTime(format(new Date(data.end_time), 'HH:mm'));
      setApprovalStatus(data.approved || '0');
      setUserName(data.reserver);
      if (marginStart !== null) {
        setMargin(getMinutes(new Date(data.start_time)) / 10);
      }
    }
  }, [idData, reservationList]);

  const handleleft = () => {
    if (idData > 0) setId(idData - 1);
  };

  const handleRight = () => {
    if (reservationList.length - 1 > idData) setId(idData + 1);
  };

  return (
    <>
      <Card
        onClick={() => setOpenDialog(true)}
        sx={{
          ...cardBaseStyles,
          borderLeft: `5px solid ${statusStyles.text}`,
          backgroundColor: statusStyles.bg,
          marginTop: `${marginAdd}rem`,
          paddingBottom: type ? `${Math.max(0, (timeDur - 1) * 6.5)}rem` : '0rem',
          position: 'relative',
          minHeight: type ? '110px' : 'unset',
          maxHeight: type ? 'none' : '6.5rem',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <CardContent sx={{ p: type ? '12px !important' : '8px !important' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: type ? 1 : 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333', lineHeight: 1.2, pr: 3, fontSize: type ? '0.875rem' : '0.75rem' }}>
              {booking}
            </Typography>
            {reservationList.length > 1 && (
              <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                <Tooltip title={`${reservationList.length} total reservations`}>
                  <Chip
                    label={reservationList.length}
                    size="small"
                    sx={{ height: '16px', fontSize: '10px', color: '#fff', bgcolor: statusStyles.text }}
                  />
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Time & Duration Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: type ? 0.5 : 0.2 }}>
            <AccessTimeIcon sx={{ fontSize: type ? 14 : 12, mr: 0.5, color: '#666' }} />
            <Typography variant="caption" sx={{ color: '#444', fontWeight: 500, fontSize: type ? '0.75rem' : '0.65rem' }}>
              {startTime} - {endTime}
            </Typography>
            <Typography variant="caption" sx={{ ml: 'auto', fontSize: '9px', color: '#666' }}>
              {Math.floor(timeDur)}h {Math.round((timeDur - Math.floor(timeDur)) * 60)}m
            </Typography>
          </Box>

          {/* Status Badge */}
          <Box sx={{ mb: type ? 1 : 0.5 }}>
            <Chip
              label={statusStyles.label}
              size="small"
              sx={{
                height: type ? '18px' : '15px',
                fontSize: type ? '10px' : '8px',
                bgcolor: statusStyles.bg,
                color: statusStyles.text,
                border: `1px solid ${statusStyles.border}`,
                fontWeight: 600,
                px: 0.5
              }}
            />
          </Box>

          {/* User Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: type ? 'auto' : 0 }}>
            <PersonIcon sx={{ fontSize: type ? 14 : 12, mr: 0.5, color: '#666' }} />
            <Typography
              variant="caption"
              noWrap
              sx={{ color: '#555', maxWidth: '85%', fontSize: type ? '0.75rem' : '0.65rem' }}
            >
              {userName}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Reservation Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setWhy(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 }
        }}
      >
        <DialogTitle sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Reservation Details</Typography>
          <IconButton onClick={() => setOpenDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {reservationList.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3, bgcolor: '#f5f5f5', borderRadius: '8px', p: 1 }}>
              <IconButton onClick={handleleft} disabled={idData === 0} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <Typography sx={{ mx: 2, fontWeight: 700, color: '#333' }}>
                {idData + 1} of {reservationList.length}
              </Typography>
              <IconButton onClick={handleRight} disabled={idData === reservationList.length - 1} size="small">
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography variant="overline" sx={{ color: '#888', fontWeight: 800 }}>Machine</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111', lineHeight: 1.2 }}>{booking}</Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="overline" sx={{ color: '#888', fontWeight: 800 }}>Time Slot</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTimeIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{startTime} - {endTime}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="overline" sx={{ color: '#888', fontWeight: 800 }}>Status</Typography>
                <Box>
                  <Chip
                    label={statusStyles.label}
                    sx={{ bgcolor: statusStyles.bg, color: statusStyles.text, fontWeight: 700, border: `1px solid ${statusStyles.border}` }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #eee', pt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="overline" sx={{ color: '#888', fontWeight: 800 }}>Reserved By</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{userName}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="overline" sx={{ color: '#888', fontWeight: 800 }}>Project</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FolderOpenIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{reservationList[idData]?.project || 'N/A'}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {why && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#fff5f5', borderRadius: '12px', border: '1px solid #fed7d7' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: '#c53030' }}>
                Cancellation Reason
              </Typography>
              <StyledFormControl fullWidth size="small">
                <Select
                  value={whyValue}
                  onChange={e => setWhyValue(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Choose a reason...</MenuItem>
                  {whyOptions.map((o, i) => <MenuItem key={i} value={o}>{o}</MenuItem>)}
                </Select>
              </StyledFormControl>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button fullWidth variant="contained" color="error" disabled={!whyValue} sx={{ borderRadius: '8px', fontWeight: 700 }}>
                  Confirm
                </Button>
                <Button fullWidth variant="text" onClick={() => setWhy(false)} sx={{ borderRadius: '8px', color: '#666' }}>
                  Back
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          {!why && (
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={() => setWhy(true)}
              sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none' }}
            >
              Cancel Reservation
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookingCard;
